import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOrders } from '../../../store/orders/orderReducer'; // Adjust path as needed
import { RootState } from '../../../store'; // Adjust path as needed
import { useNavigate } from 'react-router-dom';
import { getMe } from '../../../store/auth/authReducer';

interface Order {
    id: string;
    createdAt: string;
    totalAmount: number;
    items: {
        packageName: string;
        quantity: number;
    }[];
    receiptUrl?: string;
    paymentStatus: string;
}

interface RecentActivity {
    date: string | null;
    amount: number;
    description: string;
    receiptUrl?: string;
    paymentStatus?: string;
}

const UserDashboard: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { token, user } = useSelector((state: RootState) => state.auth)
    const { orders, isLoading, isError } = useSelector((state: RootState) => state.orders);

    const [totalSpent, setTotalSpent] = useState<number>(0);
    const [orderCount, setOrderCount] = useState<number>(0);
    const [timeSinceLastOrder, setTimeSinceLastOrder] = useState<string>('');
    const [recentActivity, setRecentActivity] = useState<RecentActivity>({
        date: null,
        amount: 0,
        description: 'No recent activity',
    });

    // Fetch orders from Redux
    useEffect(() => {
        dispatch(getMe(token));
        if ((user as { isNewUser: boolean })?.isNewUser) {
            navigate("/guest/onboarding")
        }
        dispatch(fetchOrders(token));
    }, [dispatch]);

    useEffect(() => {
        if (orders?.length) {
            // Calculate total spent
            const total = orders.reduce((sum: number, order: Order) => sum + (order.totalAmount || 0), 0);
            setTotalSpent(total);

            // Count total orders
            setOrderCount(orders.length);

            // Calculate time since last order
            const lastOrder = orders[0]; // Assuming orders are sorted by date (most recent first)
            const lastOrderDate = new Date(lastOrder.createdAt);
            const now = new Date();
            const diffInMs = now.getTime() - lastOrderDate.getTime();
            const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
            setTimeSinceLastOrder(`${diffInDays} days ago`);

            // Set the most recent activity
            setRecentActivity({
                date: lastOrder.createdAt,
                amount: lastOrder.totalAmount,
                description: lastOrder.items
                    .map((item) => `${item.packageName} (${item.quantity}x)`)
                    .join(', '),
                receiptUrl: lastOrder.receiptUrl,
                paymentStatus: lastOrder.paymentStatus,
            });
        }
    }, [orders]);

    const cards = [
        { name: 'Total Amount Spent', count: `$${totalSpent.toFixed(2)}` },
        { name: 'Number of Orders', count: orderCount },
        { name: 'Last Order', count: timeSinceLastOrder },
    ];

    if (isLoading) {
        return <div className="text-center text-gray-500">Loading...</div>;
    }

    if (isError) {
        return <div className="text-center text-red-500">Failed to load orders.</div>;
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            {/* Overview Section */}
            <section>
                <h2 className="text-lg font-semibold text-gray-900">Overview</h2>
                <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-3">
                    {cards.map((card, index) => (
                        <div key={index} className="rounded-lg bg-white shadow p-4">
                            <h3 className="text-sm font-medium text-gray-500">{card.name}</h3>
                            <p className="mt-2 text-2xl font-semibold text-gray-900">{card.count}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Most Recent Activity Section */}
            <section className="mt-8">
                <h2 className="text-lg font-semibold text-gray-900">Most Recent Activity</h2>
                {recentActivity.date ? (
                    <div className="p-4 bg-white rounded-lg shadow space-y-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">Items</h3>
                            <p className="text-sm text-gray-700">{recentActivity.description}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <h4 className="text-sm font-semibold text-gray-600">Date</h4>
                                <p className="text-sm text-gray-700">
                                    {new Date(recentActivity.date).toLocaleString()}
                                </p>
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-600">Amount</h4>
                                <p className="text-sm text-gray-700">${recentActivity.amount.toFixed(2)}</p>
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-600">Status</h4>
                                <p className="text-sm text-gray-700">{recentActivity.paymentStatus}</p>
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-600">Receipt</h4>
                                <a
                                    href={recentActivity.receiptUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-500 underline"
                                >
                                    View Receipt
                                </a>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 bg-white rounded-lg shadow">
                        <p className="text-sm text-gray-500">No recent activity available.</p>
                    </div>
                )}
            </section>
        </div>
    );
};

export default UserDashboard;
